import {
  For,
  Show,
  batch,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  type Component,
  type ParentComponent,
} from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Wheel from '~/components/Wheel';
import Felt, { pocketsForHit } from '~/components/Felt';
import { createSpreadEditor } from '~/state/editor';
import { applyPort, planPort } from '~/domain/port';
import LibraryRail, { type RailTab } from '~/components/LibraryRail';
import PortDialog from '~/components/PortDialog';
import HonestyPanel from '~/components/HonestyPanel';
import SimulationSetup, { type SelectedEntry } from '~/components/SimulationSetup';
import ComparisonTable from '~/components/ComparisonTable';
import TrajectoryChart from '~/components/TrajectoryChart';
import LedgerTable from '~/components/LedgerTable';
import EdgeHonesty from '~/components/EdgeHonesty';
import { createStrategiesRunner } from '~/state/useSimulation';
import type { SessionRules } from '~/domain/session';
import CoveragePanel from '~/components/CoveragePanel';
import OddsBreakdown from '~/components/OddsBreakdown';
import { analyseSpread, chipsOn } from '~/domain/spread';
import { getWheel } from '~/domain/wheels';
import { betKey, betLabel, coveredPockets, isValidBet } from '~/domain/bets';
import type {
  BetType,
  CasinoProfile,
  PlacedBet,
  PocketId,
  Spread,
  TableProfile,
  WheelStyle,
} from '~/domain/types';
import type { Library } from '~/state/db';
import type { FeltHit } from '~/view/feltLayout';
import type { PreviewMode, PreviewState } from '~/view/pocketState';
import SpreadCatalogue, { type CatalogueEntry } from '~/components/SpreadCatalogue';
import StrategyComposer from '~/components/StrategyComposer';
import LayerLegend from '~/components/LayerLegend';
import { createStrategyComposer } from '~/state/composer';
import { analyseStrategy, type Strategy } from '~/domain/strategy';
import { presetsFor, type SpreadPreset } from '~/domain/presets';
import { createProfileStore } from '~/state/profileStore';
import {
  createCasino,
  createTable,
  defaultTableFor,
  deviatesFromStandard,
} from '~/domain/profiles';
import { isBeta } from '~/release';
import TableSelector from '~/components/TableSelector';
import CasinoConfigurator from '~/components/CasinoConfigurator';

/**
 * A pre-built spread, as a `Spread`.
 *
 * The catalogue holds one kind of thing, so the curated set has to arrive as
 * that kind of thing. Their ids are the preset ids, which are stable across
 * builds — that is what lets a strategy reference a pre-built spread and still
 * resolve after a reload, without any of them being written to the database.
 */
const presetAsSpread = (preset: SpreadPreset): Spread => ({
  id: preset.id,
  name: preset.name,
  wheelStyle: preset.wheelStyle,
  bets: preset.bets.map((bet) => ({ ...bet })),
  tags: [],
  createdAt: 0,
  updatedAt: 0,
});

const App: Component = () => {
  const editor = createSpreadEditor();
  const composer = createStrategyComposer();
  const profiles = createProfileStore();

  /** Setup builds things; Simulate tests them (SPEC-V2 §4, §5). */
  const [mode, setMode] = createSignal<'setup' | 'simulate'>('setup');
  /** Within Setup: editing one spread, or stacking several into a strategy. */
  const [workspace, setWorkspace] = createSignal<'spread' | 'strategy'>('spread');
  const [hoveredLayer, setHoveredLayer] = createSignal<number | null>(null);
  const [strategies, setStrategies] = createSignal<Strategy[]>([]);
  const [railTab, setRailTab] = createSignal<RailTab>('spreads');

  // -- simulation setup (SPEC-V2 §3.3) --------------------------------------
  const simulation = createStrategiesRunner();
  const [selectedEntries, setSelectedEntries] = createSignal<SelectedEntry[]>([]);
  const [sessionsPerCampaign, setSessionsPerCampaign] = createSignal(20);
  const [campaigns, setCampaigns] = createSignal(200);
  const [seed, setSeed] = createSignal('planner');
  const [focusedStrategyId, setFocusedStrategyId] = createSignal<string | null>(null);
  const [rules, setRules] = createSignal<SessionRules>({
    buyInCents: 20_000,
    // Defaults to the buy-in: everything won above what was brought to the
    // table is skimmed at once, which is the setting that actually bounds the
    // loss to the buy-in. A threshold above it puts winnings back at risk
    // before any of them can be locked away.
    pocketThresholdCents: 20_000,
    lower: { kind: 'stop-loss', floorCents: 5_000 },
    upper: { kind: 'pocket-target', cents: 10_000 },
    maxSpins: 2000,
  });
  /** Declared with the other stores: `catalogue` is a memo and memos run
   *  eagerly, so a signal it reads must exist before it is created. */
  const [spreads, setSpreads] = createSignal<Spread[]>([]);

  /** Picking a table and describing a venue are two surfaces (SPEC.md §5.1). */
  const [selectorOpen, setSelectorOpen] = createSignal(false);
  const [configuringCasinoId, setConfiguringCasinoId] = createSignal<string | null>(null);
  const [feltHit, setFeltHit] = createSignal<FeltHit | null>(null);
  const [hoveredPocket, setHoveredPocket] = createSignal<PocketId | null>(null);
  const [selectedPocket, setSelectedPocket] = createSignal<PocketId | null>(null);
  const [pocketPreview, setPocketPreview] = createSignal<PocketId | null>(null);
  /** Preview driven from the sidebar, where a hover can mean several bets. */
  const [hoverBets, setHoverBets] = createSignal<{ bets: PlacedBet[]; mode: PreviewMode } | null>(
    null,
  );
  const [shiftHeld, setShiftHeld] = createSignal(false);
  const [showShortcuts, setShowShortcuts] = createSignal(false);
  const navigate = useNavigate();
  const [heatMap] = createSignal<Readonly<Record<string, number>> | null>(null);

  const style = () => editor.wheelStyle();
  const wheel = createMemo(() => getWheel(style()));
  const table = () => profiles.activeTable();
  const currency = () => profiles.activeCasino().currency;
  const analysis = createMemo(() => analyseSpread(editor.bets(), style(), table()));

  /** Pre-built and saved spreads are one library; only editability differs. */
  const catalogue = createMemo<CatalogueEntry[]>(() => [
    ...presetsFor(style()).map((preset) => ({
      spread: presetAsSpread(preset),
      builtIn: true,
    })),
    ...spreads()
      .filter((spread) => spread.wheelStyle === style())
      .map((spread) => ({ spread, builtIn: false })),
  ]);

  /** Every spread a strategy's layers could name, pre-built ones included. */
  const resolvable = createMemo(() => catalogue().map((entry) => entry.spread));

  const strategyAnalysis = createMemo(() =>
    analyseStrategy(composer.strategy(), resolvable(), table()),
  );

  /**
   * Chips per layer for each target, for the felt's banded chips.
   *
   * Built from the layers' own bets rather than from `placeLayers`, because
   * that applies table limits and the composer must not claim anything about
   * legality — money does not exist until a chip value is chosen at simulation
   * time (SPEC-V2 §4.4).
   */
  const chipLayers = createMemo(() => {
    const map = new Map<string, number[]>();
    const layers = strategyAnalysis().layers;
    for (const layer of layers) {
      for (const bet of layer.spread.bets) {
        const key = betKey(bet);
        let shares = map.get(key);
        if (!shares) {
          shares = new Array<number>(layers.length).fill(0);
          map.set(key, shares);
        }
        shares[layer.index] = (shares[layer.index] ?? 0) + bet.chips;
      }
    }
    return map;
  });

  /** Saved strategies for the wheel currently in play. */
  const runnableStrategies = createMemo(() =>
    strategies().filter((entry) => entry.wheelStyle === style()),
  );

  const toggleEntry = (strategyId: string) =>
    setSelectedEntries((current) =>
      current.some((entry) => entry.strategyId === strategyId)
        ? current.filter((entry) => entry.strategyId !== strategyId)
        : [
            ...current,
            {
              strategyId,
              denominationCents:
                profiles.activeCasino().denominations[0] ?? table().baseUnit,
            },
          ],
    );

  /**
   * Resolve each selected strategy to the bets of its layers and hand the lot
   * to the worker in one request, so every entry shares a seed by construction
   * rather than by the caller remembering to pass the same one twice.
   */
  const runSimulation = () => {
    const byId = new Map(strategies().map((entry) => [entry.id, entry]));
    const spreadById = new Map(resolvable().map((spread) => [spread.id, spread]));
    const entries = selectedEntries().flatMap((entry) => {
      const strategy = byId.get(entry.strategyId);
      if (!strategy) return [];
      const layerBets = strategy.layers.flatMap((layer) => {
        const spread = spreadById.get(layer.spreadId);
        return spread ? [spread.bets.map((bet) => ({ ...bet }))] : [];
      });
      if (layerBets.length === 0) return [];
      return [{ strategyId: entry.strategyId, denominationCents: entry.denominationCents, layerBets }];
    });
    if (entries.length === 0) return;
    setFocusedStrategyId(entries[0]?.strategyId ?? null);
    simulation.run({
      entries,
      table: table(),
      rules: rules(),
      sessionsPerCampaign: sessionsPerCampaign(),
      campaigns: campaigns(),
      seed: seed(),
    });
  };

  const focusedRun = createMemo(() => {
    const runs = simulation.result();
    if (!runs || runs.length === 0) return null;
    return runs.find((run) => run.stats.strategyId === focusedStrategyId()) ?? runs[0] ?? null;
  });

  /** Layer names for the focused run's ledger columns, in stack order. */
  const focusedLayerNames = createMemo(() => {
    const run = focusedRun();
    if (!run) return [];
    const strategy = strategies().find((entry) => entry.id === run.stats.strategyId);
    if (!strategy) return [];
    const spreadById = new Map(resolvable().map((spread) => [spread.id, spread]));
    return strategy.layers.flatMap((layer) => {
      const spread = spreadById.get(layer.spreadId);
      return spread ? [spread.name] : [];
    });
  });

  /**
   * Take the current stack to a table.
   *
   * A strategy is what you play, so that is what read-only mode opens. It
   * reads from storage, so an unsaved one would arrive as whatever was last
   * saved — or as nothing at all if it has never been saved — which is why
   * this saves first rather than offering to.
   */
  const openAtTable = () => {
    if (composer.strategy().layers.length === 0) return;
    if (composer.dirty()) saveStrategy();
    navigate(`/strategy/${composer.strategyId()}/read-only`);
  };

  const nameOfStrategy = (strategyId: string) =>
    strategies().find((entry) => entry.id === strategyId)?.name ?? 'Deleted strategy';

  const composing = () => mode() === 'setup' && workspace() === 'strategy';
  const feltBets = () => (composing() ? strategyAnalysis().mergedBets : editor.bets());

  // Shift is a live modifier — the preview has to show removal before the click.
  onMount(() => {
    const onKey = (event: KeyboardEvent) => setShiftHeld(event.shiftKey);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    onCleanup(() => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    });
  });

  /**
   * Keyboard shortcuts. Ignored while a text field has focus, so typing a
   * spread name never triggers a command.
   */
  onMount(() => {
    const isTyping = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      return (
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      );
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      const accel = event.ctrlKey || event.metaKey;

      if (accel && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) editor.redo();
        else editor.undo();
        return;
      }
      if (accel && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        editor.redo();
        return;
      }
      if (accel && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveSpread();
        return;
      }
      if (accel) return;

      switch (event.key) {
        case 'Escape':
          setSelectorOpen(false);
          setConfiguringCasinoId(null);
          setPortTarget(null);
          setShowShortcuts(false);
          setSelectedPocket(null);
          break;
        case '?':
          setShowShortcuts((open) => !open);
          break;
        case 'r':
          openAtTable();
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    onCleanup(() => window.removeEventListener('keydown', onKeyDown));
  });

  const hitTarget = (hit: FeltHit): Pick<PlacedBet, 'betType' | 'target'> =>
    hit.kind === 'point'
      ? { betType: hit.point.betType, target: hit.point.target }
      : { betType: hit.region.betType, target: hit.region.target };

  const previewTarget = createMemo(() => {
    const hit = feltHit();
    if (hit) return hitTarget(hit);
    const hovered = hoverBets();
    // A ghost chip only makes sense when the preview is a single bet.
    return hovered?.bets.length === 1 ? hovered.bets[0]! : null;
  });

  /**
   * The one preview, shared by felt and wheel so both always agree.
   *
   * §3.1 state 1 is "hovering a bet already in the spread" — that reads amber,
   * because what it shows you is the footprint you would lose. Holding shift
   * (the remove modifier) does the same for a bet that is not yet placed.
   */
  const preview = createMemo<PreviewState | null>(() => {
    const hit = feltHit();
    if (hit) {
      const target = hitTarget(hit);
      const alreadyPlaced = chipsOn(editor.bets(), target) > 0;
      return {
        mode: alreadyPlaced || shiftHeld() ? 'remove' : 'add',
        pockets: new Set(pocketsForHit(hit, style())),
      };
    }
    const hovered = hoverBets();
    if (hovered) {
      const pockets = new Set(hovered.bets.flatMap((bet) => coveredPockets(bet, wheel())));
      return { mode: hovered.mode, pockets };
    }
    // Long-press on the wheel is the touch equivalent of hover.
    const pocketId = pocketPreview();
    if (pocketId) {
      const placed = chipsOn(editor.bets(), { betType: 'straight', target: pocketId }) > 0;
      return { mode: placed ? 'remove' : 'add', pockets: new Set([pocketId]) };
    }
    return null;
  });

  /** Wheel → felt: every bet covering the hovered pocket lights up. */
  const highlightBets = createMemo(() => {
    const pocketId = hoveredPocket() ?? selectedPocket();
    if (!pocketId) return new Set<string>();
    return new Set((analysis().coverage.get(pocketId)?.bets ?? []).map(betKey));
  });

  /** A listed bet is already in the spread, so it previews as a removal. */
  const previewBet = (bet: PlacedBet | null) =>
    setHoverBets(bet ? { bets: [bet], mode: 'remove' } : null);

  const place = (betType: BetType, target: string) => {
    const bet: PlacedBet = { betType, target, chips: 1 };
    if (!isValidBet(bet, style())) return;
    editor.placeBet(bet);
  };

  const remove = (betType: BetType, target: string) =>
    editor.removeChipsFrom({ betType, target }, 1);

  const switchStyle = (next: WheelStyle) => {
    if (next === style()) return;
    if (!confirmDiscard('Switch anyway?')) return;
    editor.newSpread(next);
    // Keep the active table consistent with the style — no silent conversion.
    profiles.syncToStyle(next);
    setSelectedPocket(null);
    setFeltHit(null);
  };

  /**
   * §5.1: choosing a table of the other wheel style is the same situation as
   * switching style by hand — prompt, never silently convert. Without this the
   * app would happily compute a single-zero spread against a double-zero table.
   */
  const chooseTable = (profile: TableProfile) => {
    if (profile.wheelStyle !== style()) {
      if (!confirmDiscard('Switch to this table anyway?')) return;
      editor.newSpread(profile.wheelStyle);
      setSelectedPocket(null);
      setFeltHit(null);
    }
    profiles.setActiveTable(profile.id);
    setSelectorOpen(false);
  };

  // -- persistence ----------------------------------------------------------
  // Loaded lazily so the app still runs where IndexedDB is unavailable (private
  // browsing, for one). It just will not remember profiles.
  const [library, setLibrary] = createSignal<Library | null>(null);

  onMount(() => {
    void (async () => {
      try {
        const { openLibrary } = await import('~/state/db');
        const opened = await openLibrary();
        const [casinos, tables] = await Promise.all([
          opened.casinos.all(),
          opened.tables.all(),
        ]);
        batch(() => {
          if (casinos.length > 0) profiles.setCasinos(casinos);
          if (tables.length > 0) profiles.setTables(tables);
          profiles.syncToStyle(style());
        });
        setSpreads(await opened.spreads.all());
        setStrategies(await opened.strategies.all());
        setLibrary(opened);
      } catch (error) {
        console.warn('Profile storage is unavailable; running without persistence', error);
      }
    })();
  });

  const report = (operation: Promise<unknown>) =>
    void operation.catch((error: unknown) => console.error('Could not save profile', error));

  // Store and database move together, so what the app shows is what it kept.
  // The editors report an intent; the mutation belongs here, with persistence.

  const saveCasino = (casino: CasinoProfile) => {
    profiles.upsertCasino(casino);
    const opened = library();
    if (opened) report(opened.casinos.put(casino));
  };

  const saveTable = (profile: TableProfile) => {
    profiles.upsertTable(profile);
    const opened = library();
    if (opened) report(opened.tables.put(profile));
  };

  const dropCasino = (casinoId: string) => {
    profiles.removeCasino(casinoId);
    const opened = library();
    if (opened) report(opened.casinos.remove(casinoId));
  };

  const dropTable = (tableId: string) => {
    profiles.removeTable(tableId);
    const opened = library();
    if (opened) report(opened.tables.remove(tableId));
  };

  const configuringCasino = createMemo(
    () => profiles.casinos().find((casino) => casino.id === configuringCasinoId()) ?? null,
  );

  const addCasino = () => {
    const casino = createCasino('New casino');
    saveCasino(casino);
    // A casino with no table cannot be selected, so give it one straight away.
    saveTable(createTable(casino.id, 'Table 1', 'single-zero'));
    setSelectorOpen(false);
    setConfiguringCasinoId(casino.id);
  };

  const addTable = (casinoId: string, nextStyle: WheelStyle) => {
    const profile = createTable(casinoId, 'New table', nextStyle);
    saveTable(profile);
    return profile;
  };

  const copyTable = (profile: TableProfile) => {
    const copy = profiles.copyTable(profile, `${profile.name} copy`);
    const opened = library();
    if (opened) report(opened.tables.put(copy));
    return copy;
  };

  // -- the spread library ---------------------------------------------------

  const [portTarget, setPortTarget] = createSignal<WheelStyle | null>(null);

  const refreshSpreads = async (opened: Library) => setSpreads(await opened.spreads.all());
  const refreshStrategies = async (opened: Library) =>
    setStrategies(await opened.strategies.all());

  const saveStrategy = () => {
    const strategy = composer.toStrategy();
    composer.markSaved();
    const opened = library();
    if (!opened) return;
    report(opened.strategies.put(strategy).then(() => refreshStrategies(opened)));
  };

  const openStrategy = (strategy: Strategy) => {
    if (composer.dirty() && !window.confirm('This strategy has unsaved changes that will be discarded. Continue?')) return;
    composer.loadStrategy(strategy);
    profiles.syncToStyle(strategy.wheelStyle);
    setWorkspace('strategy');
  };

  const deleteStrategy = (strategy: Strategy) => {
    if (!window.confirm(`Delete "${strategy.name}"? This cannot be undone.`)) return;
    const opened = library();
    if (!opened) return;
    report(opened.strategies.remove(strategy.id).then(() => refreshStrategies(opened)));
  };

  const saveSpread = () => {
    const opened = library();
    const spread: Spread = editor.toSpread();
    editor.markSaved();
    if (!opened) return;
    report(opened.spreads.put(spread).then(() => refreshSpreads(opened)));
  };

  const confirmDiscard = (action = 'Continue?') =>
    !editor.dirty() ||
    window.confirm(`This spread has unsaved changes that will be discarded. ${action}`);


  /**
   * Open a catalogue row in the editor.
   *
   * A pre-built spread is reference data, so it opens as a *starting point*: a
   * new spread carrying its bets, which saves as one of the user's own. Loading
   * it under its own id would let the next save quietly overwrite a preset that
   * is supposed to be the thing everything else is measured against.
   */
  const openCatalogueSpread = (entry: CatalogueEntry) => {
    if (!confirmDiscard()) return;
    if (entry.builtIn) {
      editor.newSpread(entry.spread.wheelStyle);
      editor.replaceBets(entry.spread.bets, entry.spread.name);
      editor.setName(entry.spread.name);
    } else {
      editor.loadSpread(entry.spread);
    }
    setSelectedPocket(null);
    setFeltHit(null);
  };

  const startSpread = (nextStyle: WheelStyle) => {
    if (!confirmDiscard()) return;
    editor.newSpread(nextStyle);
    profiles.syncToStyle(nextStyle);
  };



  const deleteSpread = (spread: Spread) => {
    const opened = library();
    if (!opened) return;
    report(opened.spreads.remove(spread.id).then(() => refreshSpreads(opened)));
  };

  const exportLibrary = () => {
    const opened = library();
    if (!opened) return;
    report(
      opened.exportJson().then((json) => {
        const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `roulette-spreads-${new Date().toISOString().slice(0, 10)}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
      }),
    );
  };

  /**
   * Beta escape hatch (SPEC.md §5.1). Wider than the library — profiles and
   * spin logs go too — which is why it lives in the header rather than the rail.
   *
   * Clears what the app is holding *and then* reloads. The reload alone would
   * do it, but relying on it made the wipe invisible whenever the reload was
   * slow or did not happen: the lists on screen still named records the
   * database no longer had, which reads exactly like a reset that failed. The
   * in-memory clear is the part the user can see, so it happens first.
   */
  const resetLibrary = () => {
    const opened = library();
    if (!opened) return;
    if (
      !window.confirm(
        'Reset everything? Every saved spread, strategy, casino and table is deleted, the built-in profiles go back to standard, and the page reloads. Export first if you want a copy — this cannot be undone.',
      )
    ) {
      return;
    }
    report(
      opened.reset().then(() => {
        batch(() => {
          setSpreads([]);
          setStrategies([]);
          composer.newStrategy(style());
          editor.newSpread(style());
          profiles.setActiveTable(defaultTableFor(style()).id);
        });
        window.location.reload();
      }),
    );
  };

  const importLibrary = (file: File) => {
    const opened = library();
    if (!opened) return;
    report(
      file
        .text()
        .then((json) => opened.importJson(json, 'merge'))
        .then(async (result) => {
          await refreshSpreads(opened);
          const [loadedCasinos, loadedTables] = await Promise.all([
            opened.casinos.all(),
            opened.tables.all(),
          ]);
          batch(() => {
            profiles.setCasinos(loadedCasinos);
            profiles.setTables(loadedTables);
          });
          const summary = `Imported ${result.spreads} spreads, ${result.casinos} casinos, ${result.tables} tables.${
            result.skipped > 0 ? ` Skipped ${result.skipped} already present.` : ''
          }`;
          window.alert(
            result.errors.length > 0 ? `${summary}\n\nProblems:\n${result.errors.join('\n')}` : summary,
          );
        }),
    );
  };

  // -- porting between styles (§5) ------------------------------------------

  const portReport = createMemo(() => {
    const target = portTarget();
    if (!target) return null;
    return planPort(editor.bets(), style(), target, {
      from: table(),
      to: profiles.tablesForStyle(target)[0],
    });
  });

  const confirmPort = () => {
    const plan = portReport();
    if (!plan) return;
    const ported = applyPort(editor.toSpread(), plan);
    setPortTarget(null);
    editor.loadSpread(ported);
    profiles.syncToStyle(ported.wheelStyle);
    const opened = library();
    if (opened) report(opened.spreads.put(ported).then(() => refreshSpreads(opened)));
  };

  return (
    <div class="flex min-h-full flex-col">
      <header class="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-line bg-surface-1 px-4 py-2.5">
        <h1 class="text-[13px] font-medium tracking-tight text-ink">Strategy Tester</h1>

        {/* Primary navigation: Setup builds things, Simulate tests them. */}
        <div class="flex overflow-hidden rounded border border-line-strong">
          <For each={['setup', 'simulate'] as const}>
            {(candidate) => (
              <button
                type="button"
                onClick={() => setMode(candidate)}
                class="px-3 py-1 text-[11px] capitalize transition-colors"
                classList={{
                  'bg-placed text-white': mode() === candidate,
                  'bg-surface-2 text-ink-dim hover:text-ink': mode() !== candidate,
                }}
              >
                {candidate}
              </button>
            )}
          </For>
        </div>

        <div class="flex overflow-hidden rounded border border-line">
          <For each={['single-zero', 'double-zero'] as WheelStyle[]}>
            {(candidate) => (
              <button
                type="button"
                onClick={() => switchStyle(candidate)}
                class="px-2.5 py-1 text-[11px] transition-colors"
                classList={{
                  'bg-placed/25 text-ink': style() === candidate,
                  'bg-surface-2 text-ink-dim hover:text-ink': style() !== candidate,
                }}
              >
                {getWheel(candidate).label}
              </button>
            )}
          </For>
        </div>

        <div class="relative">
          <button
            type="button"
            onClick={() => setSelectorOpen((open) => !open)}
            aria-expanded={selectorOpen()}
            class="flex items-center gap-2 rounded border border-line bg-surface-2 px-2.5 py-1 text-[11px] text-ink-dim hover:border-line-strong hover:text-ink"
            title="Choose the casino and table in play"
          >
            <span class="text-ink">{profiles.activeCasino().name}</span>
            <span class="text-ink-faint">·</span>
            <span>{table().name}</span>
            <Show when={deviatesFromStandard(table())}>
              <span class="rounded bg-remove/20 px-1 text-[10px] text-remove">non-standard</span>
            </Show>
            <span class="text-ink-faint">▾</span>
          </button>

          <Show when={selectorOpen()}>
            {/* Catches the click that dismisses it, so the selector needs no
                document-level listener of its own. */}
            <div class="fixed inset-0 z-30" onClick={() => setSelectorOpen(false)} />
            <div
              class="absolute left-0 top-full z-40 mt-1 w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface-1 shadow-2xl"
              role="dialog"
              aria-label="Choose a table"
            >
              <TableSelector
                casinos={profiles.casinos()}
                tablesFor={profiles.tablesForCasino}
                activeTableId={table().id}
                onSelect={chooseTable}
                onEditCasino={(casino) => {
                  setSelectorOpen(false);
                  setConfiguringCasinoId(casino.id);
                }}
                onAddCasino={addCasino}
              />
            </div>
          </Show>
        </div>

        <div class="flex gap-1">
          <ToolButton onClick={editor.undo} disabled={!editor.canUndo()} title="Undo">
            Undo
          </ToolButton>
          <ToolButton onClick={editor.redo} disabled={!editor.canRedo()} title="Redo">
            Redo
          </ToolButton>
          <ToolButton onClick={editor.clearAll} disabled={editor.bets().length === 0} title="Clear">
            Clear
          </ToolButton>
        </div>

        <div class="flex items-center gap-2">
          <input
            type="text"
            value={editor.name()}
            onInput={(event) => editor.setName(event.currentTarget.value)}
            aria-label="Spread name"
            class="w-40 rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
          />
          <ToolButton onClick={saveSpread} disabled={!editor.dirty()} title="Save this spread">
            {editor.dirty() ? 'Save' : 'Saved'}
          </ToolButton>
          <ToolButton
            onClick={() => setPortTarget(style() === 'single-zero' ? 'double-zero' : 'single-zero')}
            disabled={editor.bets().length === 0}
            title="Port this spread to the other wheel style"
          >
            Port…
          </ToolButton>
          <ToolButton
            onClick={openAtTable}
            disabled={composer.strategy().layers.length === 0}
            title="Open this strategy read-only for use at a table (r)"
          >
            At the table
          </ToolButton>
        </div>

        <div class="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowShortcuts(true)}
            class="text-[11px] text-ink-faint hover:text-ink"
            title="Keyboard shortcuts"
          >
            Click to place · shift-click or right-click to remove · <kbd>?</kbd>
          </button>

          {/* App-level, not library-level: this clears profiles and spin logs
              as well as spreads. The badge is what explains the button. */}
          <Show when={isBeta()}>
            <div class="flex items-center gap-1.5">
              <span class="rounded bg-surface-3 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-ink-faint">
                beta
              </span>
              <button
                type="button"
                onClick={resetLibrary}
                title="Beta only — delete every spread, casino, table and spin log, and start clean"
                class="rounded border border-line bg-surface-2 px-2 py-1 text-[10px] text-ink-faint hover:border-loss hover:text-loss"
              >
                Reset all data
              </button>
            </div>
          </Show>
        </div>
      </header>

      <Show when={mode() === 'setup'}>
      <main class="flex flex-1 flex-col gap-5 p-4 xl:flex-row">
        <LibraryRail
          activeTab={railTab()}
          onTabChange={setRailTab}
          savedCount={strategies().length}
          onExport={exportLibrary}
          onImport={importLibrary}
          class="w-full shrink-0 xl:w-[260px]"
        >
          <Show when={railTab() === 'spreads'}>
            <SpreadCatalogue
              entries={catalogue()}
              checkAdd={composing() ? (spread) => composer.canAdd(spread) : undefined}
              activeSpreadId={composing() ? null : editor.spreadId()}
              onActivate={(entry) => {
                if (composing()) composer.addSpread(entry.spread);
                else openCatalogueSpread(entry);
              }}
              onPreview={(entry) =>
                setHoverBets(entry ? { bets: [...entry.spread.bets], mode: 'add' } : null)
              }
              onDelete={deleteSpread}
            />
          </Show>

          <Show when={railTab() === 'strategies'}>
            <div class="space-y-1">
              <For each={strategies().filter((entry) => entry.wheelStyle === style())}>
                {(entry) => (
                  <div
                    class="group cursor-pointer rounded border px-2 py-1.5 transition-colors"
                    classList={{
                      'border-placed bg-placed/15': entry.id === composer.strategyId(),
                      'border-line bg-surface-2 hover:border-line-strong':
                        entry.id !== composer.strategyId(),
                    }}
                    onClick={() => openStrategy(entry)}
                  >
                    <div class="flex items-baseline justify-between gap-2">
                      <span class="truncate text-[11px] text-ink">{entry.name}</span>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteStrategy(entry);
                        }}
                        class="shrink-0 rounded border border-line px-1.5 py-0.5 text-[10px] text-ink-faint hover:border-loss hover:text-loss"
                      >
                        Delete
                      </button>
                    </div>
                    <p class="tnum mt-0.5 text-[10px] text-ink-faint">
                      {entry.layers.length}{' '}
                      {entry.layers.length === 1 ? 'spread' : 'spreads'}
                    </p>
                  </div>
                )}
              </For>
              <Show when={strategies().filter((entry) => entry.wheelStyle === style()).length === 0}>
                <p class="px-1 py-2 text-[10px] text-ink-faint">
                  No strategies yet. Stack some spreads and save.
                </p>
              </Show>
            </div>
          </Show>
        </LibraryRail>

        <section class="flex flex-col gap-4 xl:flex-1">
          <div class="mx-auto aspect-square w-full max-w-[520px]">
            <Wheel
              wheel={wheel()}
              analysis={composing() ? strategyAnalysis().combined : analysis()}
              preview={preview()}
              selected={selectedPocket()}
              hovered={hoveredPocket()}
              heatMap={heatMap()}
              onHoverPocket={setHoveredPocket}
              onSelectPocket={setSelectedPocket}
              onPreviewPocket={setPocketPreview}
            />
          </div>

          <div class="w-full overflow-x-auto">
            <div class="min-w-[620px]">
              <Felt
                style={style()}
                bets={feltBets()}
                preview={preview()}
                previewTarget={previewTarget()}
                highlightBets={highlightBets()}
                chipLayers={composing() ? chipLayers() : undefined}
                dimLayersExcept={composing() ? hoveredLayer() : null}
                onHoverTarget={setFeltHit}
                {...(composing() ? {} : { onPlace: place, onRemove: remove })}
              />
            </div>
          </div>

          {/* What makes the colours mean anything. Only worth space when the
              felt is actually wearing them. */}
          <Show when={composing()}>
            <LayerLegend
              names={strategyAnalysis().layers.map((layer) => layer.spread.name)}
              hoveredLayer={hoveredLayer()}
              onHoverLayer={setHoveredLayer}
              class="mx-auto"
            />
          </Show>
        </section>

        <aside class="w-full shrink-0 space-y-4 xl:w-[300px]">
          {/* Within Setup, one thing is being worked on at a time: a spread is
              built chip by chip on an editable felt, a strategy is stacked from
              spreads on a read-only one. The felt cannot usefully be both. */}
          <Show when={mode() === 'setup'}>
            <div class="flex items-center gap-2">
              <div class="flex flex-1 overflow-hidden rounded border border-line text-[11px]">
                <For each={['spread', 'strategy'] as const}>
                  {(tab) => (
                    <button
                      type="button"
                      onClick={() => setWorkspace(tab)}
                      class="flex-1 px-2 py-1 capitalize transition-colors"
                      classList={{
                        'bg-placed/25 text-ink': workspace() === tab,
                        'bg-surface-2 text-ink-dim hover:text-ink': workspace() !== tab,
                      }}
                    >
                      {tab}
                    </button>
                  )}
                </For>
              </div>
              <button
                type="button"
                onClick={() => (composing() ? composer.newStrategy(style()) : startSpread(style()))}
                class="shrink-0 rounded border border-line bg-surface-2 px-2 py-1 text-[10px] text-ink-dim hover:border-line-strong hover:text-ink"
              >
                + New
              </button>
            </div>
          </Show>

          <Show when={composing()}>
            <StrategyComposer
              strategy={composer.strategy()}
              analysis={strategyAnalysis()}
              dirty={composer.dirty()}
              onRename={composer.setName}
              onRemoveLayer={composer.removeSpread}
              onReorder={composer.reorder}
              onHoverLayer={setHoveredLayer}
              onSave={saveStrategy}
            />
          </Show>

          <Show when={!composing()}>
          <Panel title={`Bets (${editor.bets().length})`}>
            <Show
              when={editor.bets().length > 0}
              fallback={<p class="text-[11px] text-ink-faint">No chips placed yet.</p>}
            >
              <ul class="max-h-64 space-y-0.5 overflow-y-auto">
                <For each={editor.bets()}>
                  {(bet) => (
                    <li>
                      <button
                        type="button"
                        onMouseEnter={() => previewBet(bet)}
                        onMouseLeave={() => previewBet(null)}
                        onClick={() => editor.clearBet(bet)}
                        class="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-[11px] transition-colors"
                        classList={{
                          'bg-placed/20 text-ink': highlightBets().has(betKey(bet)),
                          'text-ink-dim hover:bg-surface-2': !highlightBets().has(betKey(bet)),
                        }}
                        title="Click to remove this bet"
                      >
                        <span class="truncate">{betLabel(bet)}</span>
                        <span class="tnum shrink-0 text-ink">{bet.chips}</span>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Panel>

          <HonestyPanel analysis={analysis()} currency={currency()} />
          <CoveragePanel
            analysis={analysis()}
            currency={currency()}
            selectedPocket={hoveredPocket() ?? selectedPocket()}
          />
          <OddsBreakdown analysis={analysis()} currency={currency()} />
          </Show>
        </aside>
      </main>
      </Show>

      {/* Simulate: the setup on the left, the results filling the rest. The
          comparison is a table and the charts are per strategy — one chart
          holding several strategies stops being readable at three (§7.1). */}
      <Show when={mode() === 'simulate'}>
        <main class="flex flex-1 flex-col gap-5 p-4 xl:flex-row">
          <div class="w-full shrink-0 xl:w-[320px]">
            <SimulationSetup
              strategies={runnableStrategies()}
              selected={selectedEntries()}
              casino={profiles.activeCasino()}
              table={table()}
              rules={rules()}
              sessionsPerCampaign={sessionsPerCampaign()}
              campaigns={campaigns()}
              seed={seed()}
              running={simulation.running()}
              progress={simulation.progress()}
              onToggle={toggleEntry}
              onDenomination={(strategyId, cents) =>
                setSelectedEntries((current) =>
                  current.map((entry) =>
                    entry.strategyId === strategyId
                      ? { ...entry, denominationCents: cents }
                      : entry,
                  ),
                )
              }
              onRules={setRules}
              onSessionsPerCampaign={setSessionsPerCampaign}
              onCampaigns={setCampaigns}
              onSeed={setSeed}
              onRun={runSimulation}
              onCancel={simulation.cancel}
            />
          </div>

          <section class="min-w-0 flex-1 space-y-4">
            <Show when={simulation.error()}>
              {(message) => (
                <p class="rounded border border-loss/40 bg-loss/10 p-2 text-[11px] text-loss">
                  {message()}
                </p>
              )}
            </Show>

            <Show
              when={simulation.result()?.length}
              fallback={
                <p class="rounded-lg border border-line bg-surface-1 p-4 text-[11px] text-ink-faint">
                  Choose one or more strategies and run them. They share a seed, a buy-in
                  and the same walk-away markers, so what differs in the results is the
                  strategy rather than the luck or the rules.
                </p>
              }
            >
              <ComparisonTable
                runs={simulation.result() ?? []}
                nameOf={nameOfStrategy}
                selectedStrategyId={focusedRun()?.stats.strategyId ?? null}
                onSelect={setFocusedStrategyId}
                currency={currency()}
              />

              <EdgeHonesty runs={simulation.result() ?? []} nameOf={nameOfStrategy} />

              <Show when={focusedRun()}>
                {(run) => (
                  <>
                    <TrajectoryChart
                      trace={run().trace}
                      buyInCents={rules().buyInCents}
                      currency={currency()}
                      strategyName={nameOfStrategy(run().stats.strategyId)}
                    />
                    <LedgerTable
                      trace={run().trace}
                      layerNames={focusedLayerNames()}
                      currency={currency()}
                    />
                  </>
                )}
              </Show>
            </Show>
          </section>
        </main>
      </Show>

      <Show when={showShortcuts()}>
        <div
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setShowShortcuts(false)}
        >
          <div
            class="w-full max-w-sm rounded-lg border border-line bg-surface-1 p-4"
            role="dialog"
            aria-label="Keyboard shortcuts"
          >
            <h2 class="mb-3 text-[12px] font-medium text-ink">Keyboard shortcuts</h2>
            <dl class="space-y-1.5 text-[11px]">
              <For
                each={[
                  ['Ctrl / ⌘ + Z', 'Undo'],
                  ['Ctrl / ⌘ + Shift + Z', 'Redo'],
                  ['Ctrl / ⌘ + S', 'Save spread'],
                          ['r', 'Open read-only mode at a table'],
                  ['Esc', 'Close panels and clear selection'],
                  ['?', 'This list'],
                ]}
              >
                {([keys, description]) => (
                  <div class="flex items-baseline justify-between gap-3">
                    <dt class="shrink-0 rounded bg-surface-3 px-1.5 py-0.5 font-mono text-[10px] text-ink">
                      {keys}
                    </dt>
                    <dd class="text-right text-ink-dim">{description}</dd>
                  </div>
                )}
              </For>
            </dl>
            <p class="mt-3 border-t border-line pt-2 text-[10px] text-ink-faint">
              Shortcuts are ignored while a text field has focus.
            </p>
          </div>
        </div>
      </Show>

      <Show when={portReport()}>
        {(plan) => (
          <div class="fixed inset-0 z-40 flex justify-end bg-black/60">
            <div
              class="h-full w-full max-w-[420px] border-l border-line bg-surface-0 shadow-2xl"
              role="dialog"
              aria-label="Port spread between wheel styles"
            >
              <PortDialog
                report={plan()}
                sourceName={editor.name()}
                onConfirm={confirmPort}
                onCancel={() => setPortTarget(null)}
              />
            </div>
          </div>
        )}
      </Show>

      <Show when={configuringCasino()}>
        {(casino) => (
          <div class="fixed inset-0 z-40 flex justify-end bg-black/60">
            <div
              class="h-full w-full max-w-[420px] border-l border-line bg-surface-0 shadow-2xl"
              role="dialog"
              aria-label={`Configure ${casino().name}`}
            >
              <CasinoConfigurator
                casino={casino()}
                tables={profiles.tablesForCasino(casino().id)}
                activeTableId={table().id}
                onChangeCasino={saveCasino}
                onDeleteCasino={() => {
                  dropCasino(casino().id);
                  setConfiguringCasinoId(null);
                }}
                onChangeTable={saveTable}
                onAddTable={(nextStyle) => addTable(casino().id, nextStyle)}
                onDuplicateTable={copyTable}
                onDeleteTable={(profile) => dropTable(profile.id)}
                onUseTable={chooseTable}
                onClose={() => setConfiguringCasinoId(null)}
              />
            </div>
          </div>
        )}
      </Show>
    </div>
  );
};

// -- presentational helpers -------------------------------------------------

const Panel: ParentComponent<{ title: string }> = (props) => (
  <section class="rounded-lg border border-line bg-surface-1 p-3">
    <h2 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
      {props.title}
    </h2>
    {props.children}
  </section>
);

const ToolButton: ParentComponent<{
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}> = (props) => (
  <button
    type="button"
    onClick={props.onClick}
    disabled={props.disabled}
    title={props.title}
    class="rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink-dim enabled:hover:border-line-strong enabled:hover:text-ink disabled:opacity-35"
  >
    {props.children}
  </button>
);

export default App;
